'use client';

import React from 'react';
import {
  Box,
  BoxProps,
  Container,
  Divider,
  Group,
  Text,
  useMantineTheme
} from '@mantine/core';
import LogoImage from '../../public/img/logo.svg';
import FAIcon, { ValidIconName } from './FAIcon';
import OpacityLink from './OpacityLink';
import { useResponsive } from '@/hooks/use-responsive';
import { useAuthContext } from '@/context/Auth';
import { useCurrentUser } from '@/hooks/use-current-user';

interface SiteFooterProps extends BoxProps {
  [prop: string]: any;
}

interface SocialLink {
  icon: ValidIconName;
  href: string;
}

const SiteFooter: React.FC<SiteFooterProps> = ({ className, ...props }) => {
  const { responsive } = useResponsive();
  const theme = useMantineTheme();
  const { state: { twoFactorAuthenticated }, dispatch } = useAuthContext();
  const [currentUser, authStatus] = useCurrentUser();

  const handleLoginClick = (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch({ type: 'OPEN_MODAL' });
  };

  const menuItems = [
    { label: 'Home', href: '/' },
    { label: 'Resources', href: '/resources' },
    { label: 'Pricing', href: '/pricing' },
  ];

  const socialLinks: SocialLink[] = [
    {
      icon: 'linkedin-in',
      href: 'https://www.linkedin.com/company/99208016/admin/feed/posts/'
    },
    {
      icon: 'facebook',
      href: 'https://www.facebook.com/HomeHealthSolutions.io'
    },
    {
      icon: 'instagram',
      href: 'https://www.instagram.com/homehealthsolutions.io/'
    },
    {
      icon: 'twitter',
      href: 'https://twitter.com/homehealthsolutions.io/'
    },
  ];

  return (
    <Box
      component="footer"
      py={100}
      style={{
        backgroundColor: theme.colors.dark[7]
      }}
      className={className}
      {...props}
    >
      <Container size="xl">
        <Group
          wrap="nowrap"
          justify="space-between"
          gap={responsive({
            xs: 30,
            md: 0,
          })}
          style={{
            flexDirection: responsive({
              xs: 'column',
              md: 'row',
            }),
          }}
        >
          {/* Logo */}
          <Box c="white" w={200}>
            <LogoImage />
          </Box>

          {/* Navigation Links */}
          <Group
            gap={30}
            style={{
              flexDirection: responsive({
                xs: 'column',
                sm: 'row',
              }),
            }}
          >
            {menuItems.map((item) => (
              <OpacityLink key={item.label} href={item.href}>
                <Text c="white" fz={14} fw={500}>
                  {item.label}
                </Text>
              </OpacityLink>
            ))}

            {/* Conditional Login/Account Link */}
            {currentUser && twoFactorAuthenticated ? (
              <OpacityLink href="/profile">
                <Text c="white" fz={14} fw={500}>
                  Account
                </Text>
              </OpacityLink>
            ) : (
              <Text
                c="white"
                fz={14}
                fw={500}
                onClick={handleLoginClick}
                style={{ cursor: 'pointer' }}
              >
                Login
              </Text>
            )}
          </Group>

          {/* Social Links */}
          <Group
            gap={20}
            w={responsive({
              xs: 'auto',
              lg: 200,
            })}
            justify={responsive({
              xs: 'center',
              md: 'flex-end',
            })}
          >
            {socialLinks.map((link) => (
              <OpacityLink
                key={link.icon}
                href={link.href}
                target="_blank"
                transition={false}
              >
                <FAIcon
                  name={link.icon}
                  prefix="fab"
                  size={16}
                  style={{
                    color: 'white'
                  }}
                />
              </OpacityLink>
            ))}
          </Group>
        </Group>

        <Divider my={50} opacity={0.1} />

        {/* Bottom Section */}
        <Group
          gap={responsive({
            xs: 20,
            md: 50,
          })}
          justify="center"
          style={{
            flexDirection: responsive({
              xs: 'column',
              md: 'row',
            }),
          }}
        >
          <Text fz={14} c="dark.1">
            © {new Date().getFullYear()} HomeHealthSolutions. All rights reserved.
          </Text>

          <OpacityLink
            href="/privacy"
          >
            <Text fz={14} c="white">
              Privacy Policy
            </Text>
          </OpacityLink>

          <OpacityLink href="/terms">
            <Text fz={14} c="white">
              Terms of Service
            </Text>
          </OpacityLink>
        </Group>
      </Container>
    </Box>
  );
};

export default SiteFooter;